import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { StatusBadge } from "@/components/app/ui-bits";
import { Pulse, ClockCounterClockwise, ArrowsClockwise, Play } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const fmt = (v) => (v == null ? "—" : formatCurrency(v));
const dt = (v) => (v ? `${formatDate(v)} ${v.slice(11, 16)}` : "—");

export default function OpsMonitorTab({ canEdit }) {
  return (
    <div className="space-y-6" data-testid="ops-monitor-tab">
      <IntegrationMonitor canEdit={canEdit} />
      <OverduePanel canEdit={canEdit} />
    </div>
  );
}

// ─────────────────────── P10 — GL integration monitor ───────────────────────
function IntegrationMonitor({ canEdit }) {
  const [summary, setSummary] = useState(null);
  const [failed, setFailed] = useState([]);
  const [events, setEvents] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dismissing, setDismissing] = useState(null);
  const [dismissReason, setDismissReason] = useState("");

  const load = async () => {
    try {
      const [s, f, e] = await Promise.all([
        api.get("/finance/integration/summary"),
        api.get("/finance/integration/events", { params: { status: "failed", limit: 100 } }),
        api.get("/finance/integration/events", { params: { limit: 40, ...(statusFilter !== "all" ? { status: statusFilter } : {}) } }),
      ]);
      setSummary(s.data);
      setFailed(f.data.items || []);
      setEvents(e.data.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = async (id) => {
    try {
      const { data } = await api.post(`/finance/integration/events/${id}/retry`);
      toast.success(`Replayed — posted ${data.entry_no}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const dismiss = async () => {
    try {
      await api.post(`/finance/integration/events/${dismissing}/dismiss`, { reason: dismissReason });
      toast.success("Event dismissed");
      setDismissing(null); setDismissReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Pulse size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">GL Integration Monitor</h3>
        <span className="text-[11px] text-slate-400">every subledger → ledger posting event, with a retryable failed queue</span>
      </div>
      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Chip label="Posted (24h)" value={summary.posted_24h} tone="emerald" testid="ops-kpi-posted" />
          <Chip label="Open failures" value={summary.open_failures} tone={summary.open_failures > 0 ? "rose" : "slate"} testid="ops-kpi-failed" />
          <Chip label="Resolved (replayed)" value={summary.counts.resolved || 0} tone="blue" testid="ops-kpi-resolved" />
          <Chip label="Duplicates blocked" value={summary.counts.duplicate || 0} tone="slate" testid="ops-kpi-duplicates" />
        </div>
      )}

      {failed.length > 0 && (
        <div className="dc-card overflow-hidden border-l-4 border-l-rose-500" data-testid="ops-failed-queue">
          <div className="border-b border-slate-100 px-5 py-3">
            <h4 className="font-display text-sm font-semibold text-rose-700">Failed-event queue — needs attention</h4>
            <div className="text-[11px] text-slate-400">Fix the blocker (open the period, add the missing account…) then Retry to replay the exact journal.</div>
          </div>
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[1000px]">
              <thead><tr><th>When</th><th>Module</th><th>Memo</th><th>Reason</th><th className="text-right">Amount</th><th>Retries</th><th className="w-44">Actions</th></tr></thead>
              <tbody>
                {failed.map((ev) => (
                  <tr key={ev.id} className="text-sm" data-testid={`ops-failed-${ev.id}`}>
                    <td className="px-5 py-2.5 font-mono-alt text-xs tabular-nums">{dt(ev.at)}</td>
                    <td className="px-5 py-2.5"><span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono-alt text-[10px] font-bold uppercase">{ev.source_module}</span></td>
                    <td className="px-5 py-2.5 text-xs">{ev.memo}</td>
                    <td className="px-5 py-2.5 text-xs text-rose-600">{ev.reason}</td>
                    <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{fmt(ev.amount)}</td>
                    <td className="px-5 py-2.5 font-mono-alt text-xs">{ev.retry_count || 0}</td>
                    <td className="px-5 py-2.5">
                      {canEdit && (
                        dismissing === ev.id ? (
                          <div className="flex gap-1">
                            <Input className="h-7 w-36 text-xs" placeholder="Reason…" value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} data-testid="ops-dismiss-reason" />
                            <Button size="sm" className="h-7 text-xs" variant="outline" onClick={dismiss} disabled={!dismissReason.trim()} data-testid="ops-dismiss-confirm">OK</Button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Button size="sm" className="btn-primary-blue h-7 text-xs" onClick={() => retry(ev.id)} data-testid={`ops-retry-${ev.id}`}>
                              <ArrowsClockwise size={11} className="mr-1" />Retry
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setDismissing(ev.id); setDismissReason(""); }} data-testid={`ops-dismiss-${ev.id}`}>Dismiss</Button>
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h4 className="font-display text-sm font-semibold">Event log</h4>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="ops-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="duplicate">Duplicate</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {events.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500">No integration events yet — they are recorded from every new posting.</div>
        ) : (
          <div className="responsive-table-wrap max-h-96 overflow-y-auto">
            <table className="data-table w-full min-w-[900px]">
              <thead><tr><th>When</th><th>Status</th><th>Module</th><th>Memo</th><th className="text-right">Amount</th><th>Journal</th></tr></thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id} className="text-xs" data-testid={`ops-event-${ev.id}`}>
                    <td className="px-5 py-2 font-mono-alt tabular-nums">{dt(ev.at)}</td>
                    <td className="px-5 py-2"><StatusBadge status={ev.status === "posted" ? "approved" : ev.status === "failed" ? "rejected" : ev.status} /></td>
                    <td className="px-5 py-2 font-mono-alt uppercase">{ev.source_module}</td>
                    <td className="px-5 py-2">{ev.memo}{ev.reason && <span className="text-rose-500"> · {ev.reason}</span>}</td>
                    <td className="px-5 py-2 text-right font-mono-alt tabular-nums">{fmt(ev.amount)}</td>
                    <td className="px-5 py-2 font-mono-alt">{ev.entry_no || ev.resolved_entry_no || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────── P9 — overdue processing ───────────────────────
function OverduePanel({ canEdit }) {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/finance/overdue/status"),
        api.get("/finance/overdue/runs"),
      ]);
      setStatus(s.data);
      setRuns(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/finance/overdue/run");
      toast.success(`Overdue run complete — ${data.marked} document(s) marked`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ClockCounterClockwise size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Overdue Processing</h3>
        <span className="text-[11px] text-slate-400">controlled daily job · preserves original due dates · full status history</span>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="dc-card p-4 border-l-4 border-l-[#1E5CBA]">
          <div className="overline">Schedule</div>
          <div className="mt-1 text-sm font-semibold" data-testid="overdue-schedule">Daily · 00:05 UTC</div>
          <div className="text-xs text-slate-500">next run {status ? dt(status.next_scheduled_run) : "—"}</div>
          {canEdit && (
            <Button size="sm" className="btn-primary-blue mt-2 h-8" onClick={runNow} disabled={running} data-testid="overdue-run-now">
              <Play size={12} className="mr-1" /> {running ? "Running…" : "Run now"}
            </Button>
          )}
        </div>
        <div className="dc-card p-4">
          <div className="overline">Last run</div>
          {status?.last_run ? (
            <>
              <div className="mt-1 text-sm font-semibold" data-testid="overdue-last-run">{dt(status.last_run.started_at)} · {status.last_run.trigger}</div>
              <div className="text-xs text-slate-500">{status.last_run.marked} marked of {status.last_run.scanned} candidates · by {status.last_run.triggered_by}</div>
            </>
          ) : <div className="mt-1 text-sm text-slate-400">never run yet</div>}
        </div>
        <div className="dc-card p-4">
          <div className="overline">Awaiting marking</div>
          <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${status?.pending_total > 0 ? "text-amber-600" : "text-emerald-700"}`} data-testid="overdue-pending">
            {status ? status.pending_total : "—"}
          </div>
          <div className="text-xs text-slate-500">
            {status && `AR ${status.pending_marking.ar_invoices} · AP ${status.pending_marking.ap_bills} · entries ${status.pending_marking.finance_entries}`}
          </div>
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3"><h4 className="font-display text-sm font-semibold">Run history</h4></div>
        {runs.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500">No runs recorded yet.</div>
        ) : (
          <table className="data-table w-full">
            <thead><tr><th>Started</th><th>Trigger</th><th>By</th><th className="text-right">Candidates</th><th className="text-right">Marked</th><th>Breakdown</th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="text-sm" data-testid={`overdue-run-${r.id}`}>
                  <td className="px-5 py-2.5 font-mono-alt text-xs tabular-nums">{dt(r.started_at)}</td>
                  <td className="px-5 py-2.5"><span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono-alt text-[10px] font-bold uppercase">{r.trigger}</span></td>
                  <td className="px-5 py-2.5 text-xs">{r.triggered_by}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{r.scanned}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums font-semibold">{r.marked}</td>
                  <td className="px-5 py-2.5 text-[11px] text-slate-500">
                    AR {r.details?.ar_invoices?.marked ?? 0} · AP {r.details?.ap_bills?.marked ?? 0} · entries {r.details?.finance_entries?.marked ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Chip({ label, value, tone, testid }) {
  const color = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : tone === "blue" ? "text-[#1E5CBA]" : "text-slate-900";
  return (
    <div className="dc-card p-4">
      <div className="overline">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${color}`} data-testid={testid}>{value}</div>
    </div>
  );
}
